import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import type { Locale } from "@/lib/i18n/config";
import { useSellerByIdQuery } from "../services/useSellerByIdQuery";
import { useSessionQuery } from "@/shared/services/useSessionQuery";
import { useSubscriptionsQuery } from "@/shared/services/useSubscriptionsQuery";
import { BackButton } from "@/shared/layout/BackButton";
import { SellerEditForm } from "../components/SellerEditForm";
import { StoreCreateModal } from "../components/StoreCreateModal";
import { ImpersonateButton } from "@/shared/components/ImpersonateButton";
import { InviteReissueButton } from "../components/InviteReissueButton";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";
import { LoadingSkeleton, ErrorState, SectionCard } from "@a2z/ui";

type SellerDetailPageProps = {
  locale: Locale;
  sellerId: string;
};

export function SellerDetailPage({
  locale,
  sellerId,
}: SellerDetailPageProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { data: session } = useSessionQuery();
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useSellerByIdQuery({ sellerId });
  const { data: plansData, isLoading: isPlansLoading } = useSubscriptionsQuery();
  const sellerErrorStatus = getQueryErrorStatus(error);

  useEffect(() => {
    if (sellerErrorStatus === 404) {
      navigate(`/${locale}/admin/sellers`, { replace: true });
    }
  }, [locale, navigate, sellerErrorStatus]);

  if (isLoading || isPlansLoading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (sellerErrorStatus === 404) {
    return null;
  }

  if (error || !data) {
    return <ErrorState onRetry={refetch} />;
  }

  const impersonationRoles = [
    ADMIN_ROLES.ADMIN,
    ADMIN_ROLES.SUPER_ADMIN,
    ADMIN_ROLES.CUSTOMER_SERVICE,
  ];
  const canImpersonateStore =
    session?.admin?.roles?.some((role) =>
      impersonationRoles.includes(role as (typeof impersonationRoles)[number]),
    ) ?? false;
  const plans = plansData?.items ?? [];

  return (
    <section className="flex w-full max-w-2xl flex-col gap-5 lg:px-6 lg:py-6">
      <BackButton
        label={t(keys.common.back)}
        fallbackHref={`/${locale}/admin/sellers`}
      />
      <SectionCard title={t(keys.admin.sellersEditTitle)}>
        <SellerEditForm
          sellerId={data.seller.id}
          defaultValues={{
            name: data.seller.name,
            email: data.seller.email,
            phone: data.seller.phone,
          }}
        />
        <div className="mt-4">
          <InviteReissueButton sellerId={data.seller.id} locale={locale} />
        </div>
      </SectionCard>

      <SectionCard
        title={t(keys.admin.storesTitle)}
        right={
          <StoreCreateModal
            sellerId={data.seller.id}
            locale={locale}
            plans={plans}
          />
        }
      >
        <div className="grid gap-3">
          {data.stores.length === 0 ? (
            <div className="text-sm text-[color:var(--text-muted)]">
              {t(keys.common.empty)}
            </div>
          ) : (
            data.stores.map((store) => (
              <div
                key={store.id}
                className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-sm transition hover:bg-[color:var(--hover)]"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                      {store.storeName}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-[color:var(--text-muted)]">
                      <span>{store.storeSlug}</span>
                      <span className="text-[color:var(--text-disabled)]">•</span>
                      <span>
                        {t(keys.common.storeCode)}: {store.storeShort}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:shrink-0">
                    {canImpersonateStore ? (
                      <ImpersonateButton
                        storeId={store.id}
                        locale={locale}
                        adminRoles={session?.admin?.roles}
                        className="h-11 rounded-full px-4 text-sm font-semibold transition disabled:opacity-60"
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </SectionCard>
    </section>
  );
}
